import { MetricUnit } from '@/__generated__/graphql';
import { useSuspenseQuery } from '@apollo/client';
import GET_CHART_PREVIEW_DATA from '@features/deviceDataConfigurationModal/graphql/queries/getMathModelingChartPreviewData';
import GET_SENSORS from '@features/deviceDataConfigurationModal/graphql/queries/getMathModelingFrakieSensors';
import { Trans, msg } from '@lingui/macro';
import { useLingui } from '@lingui/react';
import { Box, Skeleton, TextField, Typography } from '@mui/material';
import * as math from 'mathjs';
import { Suspense, useEffect, useMemo, type FC } from 'react';
import { FieldError } from 'react-hook-form';
import { AccessorsTable } from './AccessorsTable';
import { ChartPreview, ChartPreviewSerie } from './ChartPreview';

interface Props {
  deviceId: string;
  value: string;
  onChange: React.ChangeEventHandler<HTMLInputElement | HTMLTextAreaElement>;
  error: FieldError | undefined;
  setValidator: (validator: (expression: string) => void) => void;
}

const SuspenseComponent: FC<Props> = ({ deviceId, setValidator, value, onChange, error }) => {
  const { _ } = useLingui();

  const { data } = useSuspenseQuery(GET_SENSORS, {
    variables: { input: { id: deviceId } },
  });

  const sensors = useMemo(
    () =>
      data.device.sensors.flatMap((sensor) => {
        return sensor.fields.map((field) => ({
          deviceId: sensor.deviceId,
          sensorId: sensor.telemetrySensorId,
          name: field.name,
          metric: field.name,
        }));
      }),
    [data],
  );

  useEffect(() => {
    const scope = sensors.reduce(
      (acc, sensor) => {
        const sensorMetrics = acc[sensor.deviceId] ?? {};
        acc[sensor.deviceId] = {
          ...sensorMetrics,
          [sensor.metric]: 0,
        };
        return acc;
      },
      {} as Record<string, Record<string, number>>,
    );

    setValidator((expression) =>
      math.evaluate(expression, {
        data: scope,
      }),
    );
  }, [sensors, setValidator]);

  return (
    <Box display="grid" gridTemplateColumns="minmax(0, 1fr) minmax(0, 1fr)">
      <Box
        borderRight={1}
        borderTop={1}
        borderColor="divider"
        py={2}
        pr={2}
        height={480}
        overflow="auto"
        className="custom-scrollbar"
      >
        <Typography variant="h6">
          <Trans>Modelamiento de caudal</Trans>
        </Typography>
        <Typography>
          <Trans>Expresión matemática para calcular el caudal</Trans>
        </Typography>
        <Typography>
          <Trans>Variables disponibles:</Trans>
        </Typography>
        <AccessorsTable
          metrics={sensors.map((sensor) => ({
            deviceId: sensor.deviceId,
            name: sensor.name,
            accessorKey: `data["${sensor.deviceId}"].${sensor.metric}`,
          }))}
        />
        <TextField
          value={value}
          onChange={onChange}
          size="small"
          fullWidth
          multiline
          minRows={12}
          maxRows={30}
          label={_(msg`Expresión`)}
          error={!!error}
          helperText={error?.message}
          sx={{ mt: 2 }}
        />
      </Box>
      <Box borderTop={1} borderColor="divider" py={2} pl={2}>
        <Suspense fallback={<Skeleton width="100%" height={300} variant="rounded" />}>
          <PreviewData
            deviceId={deviceId}
            expression={value}
            sensorIds={Array.from(new Set(sensors.map((sensor) => sensor.deviceId)))}
          />
        </Suspense>
      </Box>
    </Box>
  );
};

const PreviewData: FC<{ deviceId: string; sensorIds: string[]; expression: string }> = ({
  deviceId,
  sensorIds,
  expression,
}) => {
  const { data } = useSuspenseQuery(GET_CHART_PREVIEW_DATA, {
    variables: {
      input: {
        ids: Array.from(new Set([deviceId, ...sensorIds])),
      },
      metricsInput: {
        start: '-3d',
        window: '1h',
      },
    },
  });

  const series: ChartPreviewSerie[] = useMemo(() => {
    const metricsByTimestamp = new Map<number, { [deviceId: string]: { [key: string]: number } }>();

    const ids = Array.from(new Set([deviceId, ...sensorIds]));

    const series: ChartPreviewSerie[] = [];

    for (const id of ids) {
      const device = data.devices.find((device) => device.id === id);
      if (!device) continue;

      const keys = ['flow', 'level', 'position', 'angle', 'velocity'] as const;

      for (const key of keys) {
        const data = device.metrics[key].data;

        if (data.length > 0) {
          for (const [timestamp, value] of data) {
            const record = metricsByTimestamp.get(timestamp) ?? {};
            metricsByTimestamp.set(timestamp, {
              ...record,
              [device.id]: {
                ...record[device.id],
                [key]: value,
              },
            });
          }

          series.push({
            data,
            dataType: key,
            name: `${id}-${key}`,
            unit: device.metrics[key].unit,
          });
        }
      }
    }

    const calculatedData: number[][] = [];

    for (const [timestamp, value] of metricsByTimestamp) {
      try {
        const scope = { data: value, result: null as number | null };
        math.evaluate(expression, scope);
        if (scope.result != null) {
          const point = [timestamp, scope.result];
          calculatedData.push(point);
        }
      } catch (error) {
        console.error(error);
      }
    }

    series.unshift({
      data: calculatedData,
      dataType: 'flow',
      name: 'result-flow',
      unit: MetricUnit.LiterPerSecond,
    });

    return series;
  }, [data, expression, deviceId]);

  return <ChartPreview series={series} />;
};

export const FlowModelInput: FC<Props> = ({ ...props }) => (
  <Suspense>
    <SuspenseComponent {...props} />
  </Suspense>
);
